import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2, ExternalLink, Bot, Phone, Search, FileUp, UserPlus, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { SUBMIT_RESUME_URL, RECRUITER_SMS_TEL } from "../../data/recruitingLinks";
import { detectIntent } from "./intentSignals";

/**
 * Rebeca Miguel™ — AI Nurse Recruiter floating chat widget.
 * Renders a bottom-right bubble. Allowed pages only (allowlist in App.js).
 *
 * Brain: Groq (Llama 3.3 70B → Llama 3.1 8B fallback) via Vercel serverless
 * function POST /api/ai-recruiter-chat. Server-side only — GROQ_API_KEY is
 * never exposed to the browser bundle.
 */

const FRIENDLY_FALLBACK_REPLY =
  "Thanks for reaching out. I can still help you take the next step. " +
  "You can search current jobs, submit your resume, or text a recruiter below. " +
  "What specialty are you looking for?";

const QUICK_PROMPTS = [
  "I'm an ICU nurse looking for travel jobs",
  "What's the application process?",
  "Help me find local PRN shifts",
  "I want to submit my resume",
];

const STORAGE_KEY = "trital_rebeca_session";

function makeSessionId() {
  return `rebeca-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function RebecaMiguelChatbot({ variant = "floating" }) {
  const location = useLocation();
  const [open, setOpen] = useState(variant === "embedded");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm Rebeca Miguel™, TRITAL Care's AI Nurse Recruiter. Tell me your specialty, preferred location, and whether you're looking for travel, local, PRN, or permanent roles — I'll guide your next step.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  // Lead capture state
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [leadAutoPrompted, setLeadAutoPrompted] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadConnected, setLeadConnected] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", sms_opt_in: false });
  const scrollRef = useRef(null);
  const sessionId = useMemo(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (existing) return existing;
      const fresh = makeSessionId();
      localStorage.setItem(STORAGE_KEY, fresh);
      return fresh;
    } catch {
      return makeSessionId();
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Close floating chat on route change (re-opens cleanly per page).
  useEffect(() => {
    if (variant === "floating") setOpen(false);
  }, [location.pathname, variant]);

  async function send(text) {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setSending(true);
    setInput("");
    const nextMessages = [...messages, { role: "user", content: msg }];
    setMessages(nextMessages);

    // Smart auto-prompt: if the candidate's message expresses strong conversion intent,
    // surface the lead-capture form immediately (unless already connected).
    const localIntent = detectIntent(msg);
    let autoPrompted = false;
    if (localIntent.intent === "connect_recruiter" && !leadConnected && !leadFormOpen) {
      setLeadFormOpen(true);
      setLeadAutoPrompted(true);
      autoPrompted = true;
    }

    try {
      // Build the conversation payload for the serverless Groq route.
      // We send the FULL conversation history (excluding the initial greeting,
      // which is regenerated by the system prompt) so Rebeca has full context.
      const apiMessages = nextMessages
        .filter((_, idx) => idx > 0 || nextMessages[0].role !== "assistant") // drop initial greeting
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await fetch(`/api/ai-recruiter-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: apiMessages,
          pageContext: location.pathname,
          candidateContext: {},
        }),
      });
      const data = await res.json();
      const reply = data?.reply || FRIENDLY_FALLBACK_REPLY;
      // available=false means Groq is unreachable — show fallback CTAs but
      // keep the conversation flowing (no dead-end "AI unavailable" message).
      setUnavailable(data?.available === false);

      // Server-side intent detection: honor if frontend didn't already fire.
      if (
        !autoPrompted &&
        data?.intent === "connect_recruiter" &&
        !leadConnected &&
        !leadFormOpen
      ) {
        setLeadFormOpen(true);
        setLeadAutoPrompted(true);
      }

      setMessages((prev) => [...prev, { role: "assistant", content: reply, provider: data?.provider, model: data?.model }]);
    } catch {
      // Network failure → still useful fallback, never a dead end.
      setUnavailable(true);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: FRIENDLY_FALLBACK_REPLY, provider: "fallback" },
      ]);
    } finally {
      setSending(false);
    }
  }

  // Count meaningful candidate-side messages to gate the lead capture prompt.
  const userMsgCount = messages.filter((m) => m.role === "user").length;
  const shouldOfferLead = userMsgCount >= 2 && !leadConnected;

  async function submitLead(e) {
    e?.preventDefault?.();
    const name = leadForm.name.trim();
    const email = leadForm.email.trim();
    if (!name || !email) {
      toast.error("Please add your name and email so a recruiter can follow up.");
      return;
    }
    setLeadSubmitting(true);
    try {
      // Capture last 20 chat turns as transcript for the recruiter alert.
      const transcript = messages.slice(-20).map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(`/api/ai-recruiter-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          name,
          email,
          phone: leadForm.phone.trim() || null,
          sms_opt_in: leadForm.sms_opt_in,
          pageContext: location.pathname,
          transcript,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data?.ok) {
        setLeadConnected(true);
        setLeadFormOpen(false);
        toast.success("Connected — a TRITAL Care recruiter will follow up shortly.");
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              `Thanks ${name.split(" ")[0]} — your details are with our recruiting team. ` +
              "They'll review the conversation and reach out within one business day. " +
              "Feel free to keep chatting in the meantime.",
            provider: "system",
          },
        ]);
      } else {
        throw new Error("Lead create failed");
      }
    } catch {
      toast.error("Couldn't connect right now — please try again or text +1 (832) 344-1525.");
    } finally {
      setLeadSubmitting(false);
    }
  }

  const ChatBody = (
    <div
      className={`flex flex-col bg-white ${
        variant === "floating"
          ? "h-[560px] max-h-[80vh] w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-[0_30px_60px_-15px_rgba(15,23,42,0.35)] border border-slate-200"
          : "h-[640px] rounded-3xl border border-slate-200 shadow-xl"
      }`}
      data-testid="rebeca-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-t-2xl bg-trital-navy text-white">
        <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
          <Bot className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[14.5px] leading-tight">Rebeca Miguel™</div>
          <div className="text-[11px] text-white/70">AI Nurse Recruiter · TRITAL Care®</div>
        </div>
        {variant === "floating" && (
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-lg hover:bg-white/10"
            aria-label="Close chat"
            data-testid="rebeca-close-btn"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`rebeca-msg-${m.role}`}
          >
            <div
              className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-trital-navy text-white rounded-br-sm"
                  : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 inline-flex items-center gap-2 text-slate-500 text-[13px]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Rebeca is typing…
            </div>
          </div>
        )}
      </div>

      {/* Quick prompts (only initially) */}
      {messages.length <= 1 && (
        <div className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5 border-t border-slate-200 bg-white">
          {QUICK_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => send(p)}
              className="text-[11.5px] px-2.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
              data-testid={`rebeca-quick-${p.slice(0, 12).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Unavailable banner — soft notice (no dead-end). The persistent
          Jobs/Resume/Text row below still works regardless. */}
      {unavailable && (
        <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-200 text-[11.5px] text-amber-900 leading-snug" data-testid="rebeca-unavailable-banner">
          AI is taking a breath — use the quick actions below to keep moving.
        </div>
      )}

      {/* Persistent quick-action row — Jobs / Resume / Text — always visible. */}
      <div className="px-3 py-2 bg-white border-t border-slate-200 grid grid-cols-3 gap-1.5" data-testid="rebeca-quick-actions">
        <Link
          to="/careers/jobs"
          className="inline-flex items-center justify-center gap-1 text-[11.5px] font-semibold px-2 py-1.5 rounded-full bg-trital-navy text-white hover:bg-trital-navy-deep"
          data-testid="rebeca-action-jobs"
        >
          <Search className="w-3 h-3" /> Jobs
        </Link>
        <a
          href={SUBMIT_RESUME_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1 text-[11.5px] font-semibold px-2 py-1.5 rounded-full bg-trital-red text-white hover:bg-trital-red-deep"
          data-testid="rebeca-action-resume"
        >
          <FileUp className="w-3 h-3" /> Resume <ExternalLink className="w-2.5 h-2.5 opacity-70" />
        </a>
        <a
          href={`sms:${RECRUITER_SMS_TEL}`}
          className="inline-flex items-center justify-center gap-1 text-[11.5px] font-semibold px-2 py-1.5 rounded-full bg-white border border-slate-300 text-trital-navy hover:bg-slate-50"
          data-testid="rebeca-action-text"
        >
          <Phone className="w-3 h-3" /> Text
        </a>
      </div>

      {/* Lead capture — appears after 2+ candidate messages, once */}
      {shouldOfferLead && !leadFormOpen && (
        <div
          className="px-3 py-2.5 bg-trital-medical-blue-soft border-t border-trital-medical-blue-soft/40 flex items-center gap-2"
          data-testid="rebeca-lead-cta"
        >
          <UserPlus className="w-4 h-4 text-trital-navy shrink-0" strokeWidth={2} />
          <div className="flex-1 text-[12.5px] text-slate-800 leading-snug">
            <span className="font-semibold">Want a recruiter to follow up?</span>{" "}
            <span className="text-slate-600">We'll send your chat context to a TRITAL Care recruiter.</span>
          </div>
          <button
            type="button"
            onClick={() => setLeadFormOpen(true)}
            className="text-[11.5px] font-semibold px-3 py-1.5 rounded-full bg-trital-navy text-white shrink-0"
            data-testid="rebeca-lead-open-btn"
          >
            Connect
          </button>
        </div>
      )}

      {/* Lead capture — confirmation chip */}
      {leadConnected && (
        <div
          className="px-3 py-2 bg-emerald-50 border-t border-emerald-200 flex items-center gap-2"
          data-testid="rebeca-lead-confirmed"
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" strokeWidth={2} />
          <div className="text-[12.5px] text-emerald-900 leading-snug">
            <span className="font-semibold">Connected.</span> A TRITAL Care recruiter will follow up shortly.
          </div>
        </div>
      )}

      {/* Lead capture — form */}
      {leadFormOpen && !leadConnected && (
        <form
          onSubmit={submitLead}
          className={`px-3 py-3 border-t space-y-2 ${
            leadAutoPrompted
              ? "bg-trital-medical-blue-soft border-trital-medical-blue-soft/40 ring-1 ring-trital-navy/15"
              : "bg-slate-50 border-slate-200"
          }`}
          data-testid="rebeca-lead-form"
          data-auto-prompted={leadAutoPrompted ? "true" : "false"}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-[12px] font-bold text-trital-navy uppercase tracking-wider">
                Connect with a recruiter
              </div>
              {leadAutoPrompted && (
                <span
                  className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-trital-navy text-white whitespace-nowrap"
                  data-testid="rebeca-lead-suggested-pill"
                >
                  <Sparkles className="w-2.5 h-2.5" strokeWidth={2} /> Suggested
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setLeadFormOpen(false);
                setLeadAutoPrompted(false);
              }}
              className="text-[11px] text-slate-500 hover:text-slate-700 shrink-0"
              aria-label="Close lead form"
              data-testid="rebeca-lead-cancel-btn"
            >
              Cancel
            </button>
          </div>
          {leadAutoPrompted && (
            <p
              className="text-[11.5px] text-slate-700 leading-snug"
              data-testid="rebeca-lead-auto-copy"
            >
              Sounds like you're ready to take the next step — add your details and a TRITAL Care recruiter will reach out within one business day.
            </p>
          )}
          <input
            value={leadForm.name}
            onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
            placeholder="Full name"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
            required
            disabled={leadSubmitting}
            data-testid="rebeca-lead-name"
          />
          <input
            value={leadForm.email}
            onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
            placeholder="Email"
            type="email"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
            required
            disabled={leadSubmitting}
            data-testid="rebeca-lead-email"
          />
          <input
            value={leadForm.phone}
            onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
            placeholder="Phone (optional)"
            type="tel"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
            disabled={leadSubmitting}
            data-testid="rebeca-lead-phone"
          />
          <label className="flex items-start gap-2 text-[11.5px] text-slate-600 leading-snug">
            <input
              type="checkbox"
              checked={leadForm.sms_opt_in}
              onChange={(e) => setLeadForm({ ...leadForm, sms_opt_in: e.target.checked })}
              className="mt-0.5 w-3.5 h-3.5 accent-trital-navy"
              disabled={leadSubmitting}
              data-testid="rebeca-lead-sms-optin"
            />
            <span>
              I agree to receive SMS messages from TRITAL Care about job opportunities. Msg &amp; data rates may
              apply. Reply STOP to opt out. Consent is not a condition of any purchase.
            </span>
          </label>
          <button
            type="submit"
            disabled={leadSubmitting || !leadForm.name.trim() || !leadForm.email.trim()}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold disabled:opacity-50"
            data-testid="rebeca-lead-submit-btn"
          >
            {leadSubmitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Connecting…
              </>
            ) : (
              "Send to recruiter"
            )}
          </button>
        </form>
      )}

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="flex items-center gap-2 px-3 py-3 border-t border-slate-200 bg-white rounded-b-2xl"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about specialty, jobs, or next steps…"
          className="flex-1 min-w-0 px-3 py-2.5 rounded-full border border-slate-300 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
          disabled={sending}
          aria-label="Message Rebeca Miguel"
          data-testid="rebeca-input"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="w-10 h-10 rounded-full bg-trital-navy text-white flex items-center justify-center disabled:opacity-50"
          aria-label="Send message"
          data-testid="rebeca-send-btn"
        >
          <Send className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </form>

      <div className="px-4 pb-3 -mt-1 text-[10.5px] text-slate-400 leading-tight">
        AI responses are informational. A TRITAL Care recruiter performs final review.
      </div>
    </div>
  );

  if (variant === "embedded") {
    return ChatBody;
  }

  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            type="button"
            onClick={() => setOpen(true)}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ duration: 0.2 }}
            // On mobile we stack ABOVE the MobileTextRecruiterFAB (which sits at
            // bottom-4 right-4). On desktop we anchor bottom-right.
            className="fixed z-40 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-trital-navy text-white shadow-[0_18px_40px_-10px_rgba(19,39,122,0.55)] hover:bg-trital-navy-deep bottom-[88px] right-4 lg:bottom-5 lg:right-5"
            aria-label="Open Rebeca AI chat"
            data-testid="rebeca-launcher-btn"
          >
            <MessageSquare className="w-4 h-4" strokeWidth={1.9} />
            <span className="font-semibold text-[13.5px] hidden sm:inline">Ask Rebeca</span>
            <span className="font-semibold text-[13.5px] sm:hidden">Rebeca AI</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.2 }}
            // Mobile: anchor to the right edge with a safe bottom inset so the
            // panel does not cover the MobileTextRecruiterFAB or sticky CTAs.
            className="fixed z-40 bottom-4 right-4 lg:bottom-5 lg:right-5"
          >
            {ChatBody}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
